import { brandPalette } from "../../../constants/brandPalette";
import {
  Box,
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiDeleteBinLine, RiFileEditLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import { deleteOfficeHourRequest } from "../../../modules/admin-modules/office_hours/Actions";
import { openOfficeHourModal } from "../../../modules/admin-modules/models/Actions";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

function formatTs(val) {
  if (val == null || val === "") return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function cell(row, ...keys) {
  for (const k of keys) {
    if (row[k] != null && row[k] !== "") return String(row[k]);
  }
  return "—";
}

function studentSearchBits(row) {
  const st = row?.student;
  if (!st) return "";
  return [st.name, st.user_name, st.phone, st.parent_phone]
    .filter(Boolean)
    .join(" ");
}

function rowSearchText(row) {
  return [
    cell(row, "title"),
    formatTs(row.from),
    formatTs(row.to),
    cell(row, "zoom_link"),
    studentSearchBits(row),
  ]
    .join(" ")
    .toLowerCase();
}

function InfoRow({ label, value }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="700" color="rgba(69, 11, 17, 0.55)" textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Text>
      <Text fontSize="md" fontWeight="600" color={brandPalette.accentDark} mt={1}>
        {value != null && value !== "" ? String(value) : "—"}
      </Text>
    </Box>
  );
}

const OfficeHoursTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const officeHours = useSelector((state) => state.officeHours.officeHours);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.officeHours.isLoading);
  const [, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [studentModal, setStudentModal] = useState(null);

  const list = useMemo(() => {
    const raw = Array.isArray(officeHours) ? officeHours : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter((row) => rowSearchText(row).includes(q));
  }, [officeHours, searchTerm]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const viewData = (row) => {
    const plain = list.find((r) => r.id === row?.id) ?? row;
    setSelectedData(plain);
    setAdd(false);
    dispatch(openOfficeHourModal());
  };

  const Data = list.map((data) => ({
    ...data,
    id: data?.id,
    titleDisplay: cell(data, "title"),
    fromDisplay: formatTs(data.from),
    toDisplay: formatTs(data.to),
    linkDisplay: (() => {
      const link = cell(data, "zoom_link");
      if (link === "—") return link;
      return (
        <Text noOfLines={1} maxW="220px" title={link}>
          {link}
        </Text>
      );
    })(),
    studentDisplay: (() => {
      const st = data.student;
      const label = st?.name || st?.user_name;
      if (!st || !label) {
        return (
          <Text color="rgba(69, 11, 17, 0.45)" fontSize="sm">
            Not assigned
          </Text>
        );
      }
      return (
        <Button
          type="button"
          variant="link"
          color={C.primary}
          fontWeight="700"
          fontSize="sm"
          textDecoration="underline"
          textUnderlineOffset="3px"
          h="auto"
          py={0}
          minW={0}
          whiteSpace="normal"
          textAlign="left"
          onClick={(e) => {
            e.stopPropagation();
            setStudentModal(st);
          }}
        >
          {label}
        </Button>
      );
    })(),
    edit: (
      <IconButton
        {...iconBtnProps}
        aria-label="Edit office hour"
        fontSize="20px"
        icon={<RiFileEditLine />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          setAdd(false);
          dispatch(openOfficeHourModal());
        }}
      />
    ),
    delete: (
      <IconButton
        {...iconBtnProps}
        aria-label="Delete office hour"
        fontSize="20px"
        icon={<RiDeleteBinLine color="#9b2c2c" />}
        onClick={(e) => {
          e.stopPropagation();
          dispatch(deleteOfficeHourRequest(data?.id));
        }}
      />
    ),
  }));

  const columns = [
    { Header: "Title", accessor: "titleDisplay" },
    { Header: "From", accessor: "fromDisplay" },
    { Header: "To", accessor: "toDisplay" },
    { Header: "Student", accessor: "studentDisplay" },
    { Header: "Zoom link", accessor: "linkDisplay" },
    { Header: "Edit", accessor: "edit" },
    { Header: "Delete", accessor: "delete" },
  ];

  const totalPage = Math.max(1, Math.ceil(list.length / size));

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Box
        bg="rgba(255,255,255,0.9)"
        border="1px solid"
        borderColor={C.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: brandPalette.stone, color: brandPalette.accentDark }}
        />
      </Box>

      <Modal
        isOpen={Boolean(studentModal)}
        onClose={() => setStudentModal(null)}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent
          borderRadius="xl"
          border="1px solid"
          borderColor={C.border}
          bg={brandPalette.cream}
          mx={4}
        >
          <ModalHeader
            fontFamily="'Amiri','Tajawal',sans-serif"
            color={C.primary}
            fontSize="xl"
            pb={2}
          >
            Assigned student
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8}>
            <VStack align="stretch" spacing={5}>
              <InfoRow label="Name" value={studentModal?.name} />
              <InfoRow label="Username" value={studentModal?.user_name} />
              <InfoRow label="Phone" value={studentModal?.phone} />
              <InfoRow label="Parent phone" value={studentModal?.parent_phone} />
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OfficeHoursTable;
